<script setup lang="ts">
import { getTerminology } from 'src/plugins/terminology';
import { storeToRefs } from 'pinia';
import { filterConfig } from 'src/constants/configs';
import type { StatusType } from 'src/services/apiMap';
import { useGlobalSearchStore, type StatusFilter } from 'src/stores';
import type { FilterKey } from 'src/types';
import { computed, onBeforeMount, ref, watch } from 'vue';

const globalSearchStore = useGlobalSearchStore();
const { currentSearchTab, statusFilter, filterValue } = storeToRefs(globalSearchStore);
const categories = computed(() => {
    const result = [filterConfig[currentSearchTab.value] ?? undefined].filter((item) => item != undefined);
    return result;
});
const btnConfig = (label: string) => {
    return {
        label: label,
        type: 'secondary',
        size: 'small'
    };
};

const isDisableToggle = ref(false);

onBeforeMount(() => {
    filterValue.value['all_project'] = true;
    filterValue.value['all_scene'] = true;
});

const filerScene = (value: 'all' | 'my') => {
    filterValue.value['all_scene'] = value == 'all';
    filterValue.value['my_scene'] = value == 'my';
};
const getStatus = (payload: { active?: boolean; planned?: boolean; completed?: boolean; blocked?: boolean }) => {
    return Object.entries(payload).reduce((acc, [key, value]) => {
        if (value) {
            acc.push(key as StatusType);
        }
        return acc;
    }, [] as StatusType[]);
};
const getConfigFilter = (value: Partial<Record<FilterKey, boolean>>) => {
    let result: StatusFilter = {
        assigned_to_me: undefined,
        with_archived: undefined,
        status_type: undefined
    };

    switch (currentSearchTab.value) {
        case 'entity':
            result = {
                assigned_to_me: value['my_project'],
                with_archived: value['project_status:archived'],
                status_type: getStatus({
                    active: value['project_status:active'],
                    planned: value['project_status:planned'],
                    completed: value['project_status:completed']
                })
            };
            break;
        case 'item':
            result = {
                assigned_to_me: value['my_scene'],
                with_archived: value['scene_status:archived'],
                status_type: getStatus({
                    active: value['scene_status:active'],
                    planned: value['scene_status:planned'],
                    completed: value['scene_status:completed']
                })
            };
            break;
        case 'people':
            result = {
                assigned_to_me: undefined,
                with_archived: undefined,
                status_type: getStatus({
                    active: value['people_status:active'],
                    blocked: value['people_status:blocked']
                })
            };
            break;

        default:
            result = {
                assigned_to_me: undefined,
                with_archived: undefined,
                status_type: undefined
            };
            break;
    }
    return result;
};
watch(
    [() => filterValue, currentSearchTab],
    async () => {
        statusFilter.value = getConfigFilter(filterValue.value);
        isDisableToggle.value = checkFilterDisable();
    },
    { deep: true }
);
const checkFilterDisable = () => {
    const isOnlyOneActive = (value: Partial<Record<string, boolean>>) => {
        const activeCount = Object.values(value).filter((v) => v).length;
        return activeCount == 1;
    };
    if (
        currentSearchTab.value == 'entity' &&
        isOnlyOneActive({
            active: filterValue.value['project_status:active'],
            planned: filterValue.value['project_status:planned'],
            completed: filterValue.value['project_status:completed']
        })
    ) {
        return true;
    } else if (
        currentSearchTab.value == 'item' &&
        isOnlyOneActive({
            active: filterValue.value['scene_status:active'],
            planned: filterValue.value['scene_status:planned'],
            completed: filterValue.value['scene_status:completed']
        })
    ) {
        return true;
    } else {
        return false;
    }
};
watch(
    () => filterValue,
    () => {
        isDisableToggle.value = checkFilterDisable();
    },
    { deep: true, immediate: true }
);
const toggleDisable = 'To disable this, first enable another status.';
</script>
<template>
    <div
        class="global-search-filter"
        :class="{ 'no-filter': currentSearchTab == 'recent_accessed' || currentSearchTab == 'people' }">
        <h3>Filters</h3>
        <template v-if="currentSearchTab == 'recent_accessed' || currentSearchTab == 'people'">
            <span class="disabled-text">
                Not available for {{ currentSearchTab == 'recent_accessed' ? 'Recent' : 'People' }}</span
            >
        </template>
        <template v-else>
            <div v-if="currentSearchTab == 'item'" class="filter-buttons">
                <ArboButton
                    v-bind="btnConfig(`All ${getTerminology({ code: 'scene' })}`)"
                    :class="{ active: filterValue['all_scene'] }"
                    @click="filerScene('all')" />
                <ArboButton
                    v-bind="btnConfig(`My ${getTerminology({ code: 'scene' })}`)"
                    :class="{ active: filterValue['my_scene'] }"
                    @click="filerScene('my')" />
            </div>
            <div class="categories">
                <div v-for="category in categories" :key="category.type" class="category">
                    <h4 v-if="category.title" class="category-title">{{ category.title }}</h4>
                    <div class="filters">
                        <div
                            v-for="filter in category.filters"
                            :key="filter.name"
                            class="filter-item"
                            :class="{ 'break-line': !filter.value }">
                            <template v-if="filter.value">
                                <div class="filter-label">{{ filter.name }}</div>
                                <ArboToggle
                                    :id="filter.value"
                                    v-arbo-tooltip="{
                                        content:
                                            isDisableToggle && filterValue[filter.value]
                                                ? toggleDisable
                                                : filterValue[filter.value]
                                                  ? 'Hide'
                                                  : 'Show',
                                        hideOnClick: false
                                    }"
                                    :showTooltip="false"
                                    :disable="
                                        isDisableToggle &&
                                        filterValue[filter.value] &&
                                        !filter.value.includes('archived')
                                    "
                                    class="arbo-ui-styles"
                                    :value="filterValue[filter.value]"
                                    @update:value="filterValue[filter.value] = !filterValue[filter.value]" />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.filter-buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    :deep(.arbo-btn.secondary.active) {
        background-color: var(--hover-selected-color, #a3d4f5);
        background-image: unset;
    }
    :deep(.arbo-btn) {
        padding: 3px 10px;
    }

    :deep(.arbo-btn__label) {
        color: #232323;
    }
}
</style>
